// @flow
import * as React from 'react';
import classNames from 'classnames/bind';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import Select from '../../../../../elements/Select/Select';
import Cleave from 'cleave.js/dist/cleave-react';
import { FormValidator } from '../../../../../utils/form';
import CleavePhone from 'cleave.js/dist/addons/cleave-phone.i18n';
import AddressForm from '../../../../../elements/AddressForm/AddressForm';

import style from '../Payouts.scss';
import commonStyle from '../../../PreferencesViewCommon.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';
import inputStyle from '../../../../../assets/styles/input.scss';
import formStyle from '../../../../../assets/styles/form.scss';
import boxStyle from '../../../../../assets/styles/box.scss';

import type { Bank } from '../../../../../entities';
import type { Option } from '../../../../../elements/Select/Select';

type Props = {
  banks: Bank[];
  handleChangeBank: (option: Option) => void;
  handleChangeAdmin1: (option: Option) => void;
  handleChangeAdmin2: (option: Option) => void;
  handleChangeAdmin3: (option: Option) => void;
  handleChange: (e: SyntheticInputEvent<HTMLInputElement>) => void;
  handleChangePhone: (e: Event) => void;
  handleBack: (e: Event) => void;
  handleSubmit: (e: Event) => void;
  handleFocus: (e: Event) => void;
  +handlePhoneInit: (cleave: any) => void;
  isPostingPayout: boolean;
  accountNumber: {
    value: string;
    errors?: string[];
  };
  accountNumberCci: {
    value: string;
    errors?: string[];
  };
  address1: {
    value: string;
    errors?: string[];
  };
  address2: {
    value: string;
  };
  admin1: {
    value: string;
    label?: string;
    errors?: string[];
  };
  admin2: {
    value: string;
    label?: string;
    errors?: string[];
  };
  admin3: {
    value: string;
    label?: string;
    errors?: string[];
  };
  admin1Items: Location[];
  admin2Items?: Location[];
  admin3Items?: Location[];
  bank: {
    value: number;
    errors?: string[];
  };
  country: {
    value: string;
    errors?: string[];
  };
  firstName: {
    value: string;
    errors?: string[];
  };
  idNumber: {
    value: string;
    errors?: string[];
  };
  lastName: {
    value: string;
    errors?: string[];
  };
  isLoadingAdmin1?: boolean;
  isLoadingAdmin2?: boolean;
  isLoadingAdmin3?: boolean;
  phone: {
    value: string;
    errors?: string[];
  };
  routingNumber: {
    value: string;
    errors?: string[];
  };
  zipCode: {
    value: string;
    errors?: string[];
  };
};

const messages = defineMessages({
  dataAccount: {
    label: {
      id: 'preferences.payouts.dataAccount.label',
    },
  },
  bank: {
    label: {
      id: 'preferences.payouts.bank.label',
    },
    placeholder: {
      id: 'preferences.payouts.bank.placeholder',
    },
  },
  accountNumber: {
    label: {
      id: 'preferences.payouts.accountNumber.label',
    },
    placeholder: {
      id: 'preferences.payouts.accountNumber.placeholder',
    },
  },
  accountNumberCci: {
    label: {
      id: 'preferences.payouts.accountNumberCci.label',
    },
    placeholder: {
      id: 'preferences.payouts.accountNumberCci.placeholder',
    },
  },
  dataPayout: {
    label: {
      id: 'preferences.payouts.dataPayout.label',
    },
  },
  email: {
    label: {
      id: 'preferences.payouts.email.label',
    },
    placeholder: {
      id: 'preferences.payouts.email.placeholder',
    }
  },
  firstName: {
    label: {
      id: 'address.firstName.label',
    },
    placeholder: {
      id: 'address.firstName.placeholder',
    },
  },
  idNumber: {
    label: {
      id: 'address.idNumber.label',
    },
    placeholder: {
      id: 'address.idNumber.placeholder',
    }
  },
  lastName: {
    label: {
      id: 'address.lastName.label',
    },
    placeholder: {
      id: 'address.lastName.placeholder',
    },
  },
  phone: {
    label: {
      id: 'address.phone.label',
    },
    placeholder: {
      id: 'address.phone.placeholder',
    },
  },
  routingNumber: {
    label: {
      id: 'preferences.payouts.routingNumber.label',
    },
    placeholder: {
      id: 'preferences.payouts.routingNumber.placeholder',
    },
  },
  zipCode: {
    label: {
      id: 'address.zipCode.label',
    },
    placeholder: {
      id: 'address.zipCode.placeholder',
    },
  },
  button: {
    cancel: {
      id: 'preferences.payouts.button.cancel',
    },
    add: {
      id: 'preferences.payouts.button.add',
    },
  }
});

export default class AccountStep extends React.Component<Props> {

  render() {
    const {
      banks, handleChangeBank, handleChange, handleChangePhone, handleBack, handleSubmit, handleFocus, isPostingPayout,
      accountNumber, accountNumberCci, address1, address2, admin1, admin2, admin3, admin1Items, admin2Items = [],
      admin3Items = [], bank, country, firstName, idNumber, lastName, isLoadingAdmin1, isLoadingAdmin2, isLoadingAdmin3,
      phone, routingNumber, zipCode, handleChangeAdmin1, handleChangeAdmin2, handleChangeAdmin3, handlePhoneInit,
    } = this.props;
    const bankOptions: Option[] = banks && banks.map(bank => ({
      value: bank.disbursementBankId,
      label: bank.name,
    }));
    const accountNumberError = accountNumber.errors && FormValidator.buildErrorLabel(accountNumber.errors, {
      required: 'preferences.payouts.accountNumber.error.required',
    });
    const accountNumberCciError = accountNumberCci.errors && FormValidator.buildErrorLabel(accountNumberCci.errors, {
      required: 'preferences.payouts.accountNumberCci.error.required',
    });
    const bankError = bank.errors && FormValidator.buildErrorLabel(bank.errors, {
      required: 'preferences.payouts.bank.error.required',
    });
    const routingNumberError = routingNumber.errors && FormValidator.buildErrorLabel(routingNumber.errors, {
      required: 'preferences.payouts.routingNumber.error.required',
    });
    return (<section className={style.accountStep}>
      <FormattedMessage
        id="preferences.payouts.stepAccount.title">{
        txt => <h2 className={classNames(boxStyle.boxTitle, style.title)}>{txt}</h2>
      }</FormattedMessage>
      <form className={classNames(formStyle.formWrap, style.accountForm)}
            onSubmit={handleSubmit}>
        <FormattedMessage id={messages.dataAccount.label.id}>{
          txt => <p>{txt}</p>
        }</FormattedMessage>
        <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.dualWrap, formStyle.responsive)}>
          <article className={formStyle.col}>
            <FormattedMessage id={messages.bank.label.id}>{
              txt => (<label className={formStyle.label}>{txt}</label>)
            }</FormattedMessage>
            <Select options={bankOptions}
                    className={classNames({
                      'light-blue': bank.value,
                      'red': bank.errors,
                    }, style)}
                    value={bank.value}
                    disabled={isPostingPayout}
                    searchable={true}
                    placeholder={this.context.intl.formatMessage(messages.bank.placeholder)}
                    handleChange={handleChangeBank}/>
            {bankError}
          </article>
        </section>
        <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.dualWrap, formStyle.responsive)}>
          <article className={formStyle.col}>
            <FormattedMessage id={messages.accountNumber.label.id}>{
              txt => (<label className={formStyle.label}>{txt}</label>)
            }</FormattedMessage>
            <input disabled={isPostingPayout}
                   className={classNames(inputStyle.input,
                     { [inputStyle.red]: accountNumber.errors && accountNumber.errors.length },
                     { [inputStyle.lightBlue]: !!accountNumber.value })}
                   value={accountNumber.value}
                   onChange={handleChange}
                   onFocus={handleFocus}
                   name="accountNumber"
                   placeholder={this.context.intl.formatMessage(messages.accountNumber.placeholder)}/>
            {accountNumberError}
          </article>
          {country.value === 'PE' && <article className={formStyle.col}>
            <FormattedMessage id={messages.accountNumberCci.label.id}>{
              txt => (<label className={formStyle.label}>{txt}</label>)
            }</FormattedMessage>
            <input disabled={isPostingPayout}
                   className={classNames(inputStyle.input,
                     { [inputStyle.red]: accountNumberCci.errors && accountNumberCci.errors.length },
                     { [inputStyle.lightBlue]: !!accountNumberCci.value })}
                   value={accountNumberCci.value}
                   onChange={handleChange}
                   onFocus={handleFocus}
                   name="accountNumberCci"
                   placeholder={this.context.intl.formatMessage(messages.accountNumberCci.placeholder)}/>
            {accountNumberCciError}
          </article>}
          {country.value === 'US' && <article className={formStyle.col}>
            <FormattedMessage id={messages.routingNumber.label.id}>{
              txt => (<label className={formStyle.label}>{txt}</label>)
            }</FormattedMessage>
            <input disabled={isPostingPayout}
                   className={classNames(inputStyle.input,
                     { [inputStyle.red]: routingNumber.errors && routingNumber.errors.length },
                     { [inputStyle.lightBlue]: !!routingNumber.value })}
                   value={routingNumber.value}
                   onChange={handleChange}
                   onFocus={handleFocus}
                   name="routingNumber"
                   placeholder={this.context.intl.formatMessage(messages.routingNumber.placeholder)}/>
            {routingNumberError}
          </article>}
        </section>
        <FormattedMessage id={messages.dataPayout.label.id}>{
          txt => <p className={style.dataPayout}>{txt}</p>
        }</FormattedMessage>
        <AddressForm type="payout"
                     handleChangeAdmin1={handleChangeAdmin1}
                     handleChangeAdmin2={handleChangeAdmin2}
                     handleChangeAdmin3={handleChangeAdmin3}
                     handleChange={handleChange}
                     handleChangePhone={handleChangePhone}
                     handleFocus={handleFocus}
                     isPosting={isPostingPayout}
                     country={country}
                     admin1={admin1}
                     admin2={admin2}
                     admin3={admin3}
                     admin1Items={admin1Items}
                     admin2Items={admin2Items}
                     admin3Items={admin3Items}
                     isLoadingAdmin1={isLoadingAdmin1}
                     isLoadingAdmin2={isLoadingAdmin2}
                     isLoadingAdmin3={isLoadingAdmin3}
                     address1={address1}
                     address2={address2}
                     firstName={firstName}
                     idNumber={idNumber}
                     lastName={lastName}
                     phone={phone}
                     zipCode={zipCode}
                     handlePhoneInit={handlePhoneInit}/>
        <section className={classNames(commonStyle.buttonsRow, commonStyle.buttons)}>
          <FormattedMessage id={messages.button.cancel.id}>{
            txt => <button type="button"
                           disabled={isPostingPayout}
                           className={classNames(buttonStyle.btn, buttonStyle.darkGrey)}
                           onClick={handleBack}>{txt}</button>
          }</FormattedMessage>
          <FormattedMessage id={messages.button.add.id}>{
            txt => <button type="submit"
                           disabled={isPostingPayout}
                           className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}>{txt}</button>
          }</FormattedMessage>
        </section>
      </form>
    </section>);
  }
}

AccountStep.propTypes = {
  banks: PropTypes.array.isRequired,
  handleChangeBank: PropTypes.func.isRequired,
  handleChangeAdmin1: PropTypes.func.isRequired,
  handleChangeAdmin2: PropTypes.func.isRequired,
  handleChangeAdmin3: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleChangePhone: PropTypes.func.isRequired,
  handleBack: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleFocus: PropTypes.func.isRequired,
  isPostingPayout: PropTypes.bool.isRequired,
  accountNumber: PropTypes.object.isRequired,
  accountNumberCci: PropTypes.object.isRequired,
  address1: PropTypes.object.isRequired,
  address2: PropTypes.object,
  admin1: PropTypes.object.isRequired,
  admin2: PropTypes.object.isRequired,
  admin3: PropTypes.object.isRequired,
  admin1Items: PropTypes.array.isRequired,
  admin2Items: PropTypes.array,
  admin3Items: PropTypes.array,
  country: PropTypes.object.isRequired,
  bank: PropTypes.object.isRequired,
  firstName: PropTypes.object.isRequired,
  idNumber: PropTypes.object.isRequired,
  lastName: PropTypes.object.isRequired,
  isLoadingAdmin1: PropTypes.bool.isRequired,
  isLoadingAdmin2: PropTypes.bool.isRequired,
  isLoadingAdmin3: PropTypes.bool.isRequired,
  phone: PropTypes.object.isRequired,
  routingNumber: PropTypes.object.isRequired,
  zipCode: PropTypes.object.isRequired,
  handlePhoneInit: PropTypes.func.isRequired,
};

AccountStep.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/preferences/payouts/components/AccountStep/AccountStep.jsx